/**
 * PATH: packages/email-hub/src/feature/hooks/useEmails.stats.ts
 * FILE_ID: EMAILHUB-FEATURE-USE-EMAILS-STATS-001
 * PURPOSE: Optimistic email-stats delta layer — the delta shape
 *          (EmailStatsDelta), the window event + dispatch helper, and
 *          the pure bulkDeltaFor (action→delta) / mergeStatsDelta
 *          (snapshot+delta→snapshot, clamped) math.
 * WHY: Extracted from the useEmails god-hook (ARC EMAIL_HUB_LOADING_
 *      RESILIENCE phase 3, expand→migrate→contract). Pure I→O + a thin
 *      window dispatcher, no module state — zero behaviour change.
 *      Re-exported from ./useEmails so consumers stay byte-identical.
 * RELATED:
 *   - ./useEmails.types.ts: owns EmailStats (the snapshot shape).
 *   - ./useEmails.ts: useEmailStats subscribes to the event + applies
 *     the delta; useEmailActions dispatches on every mutation.
 *   - useEmailStats.optimisticDelta.pin.test.ts / .cadence.pin.test.ts.
 */
import type { EmailStats } from './useEmails.types'

/** EMAIL_STATS_OPTIMISTIC_DELTA_EVENT — Phase 2 of the
 *  "poll on change, not on a timer" rebuild (2026-05-28).
 *
 *  Phase 1 made BE polling cheap (304 short-circuit on watermark
 *  match). Phase 1 did NOT make user mutations show up instantly in
 *  the badges — between mark-read and the next watermark bump, the
 *  unread counter still showed the stale number (up to 60s, until
 *  the response-cache TTL expired and the next sync watermark
 *  arrived).
 *
 *  Phase 2 fixes that by dispatching an optimistic delta event on
 *  every mutation. ``useEmailStats`` subscribes and applies the
 *  delta to its local payload immediately. The next watermark bump
 *  reconciles against the BE truth (server addition is associative,
 *  so a temporary +/- drift is bounded and self-healing).
 *
 *  ``detail`` payload contract: a partial ``EmailStatsDelta`` where
 *  each numeric field is an INCREMENT (not a replacement). Positive
 *  means "more", negative means "less". The merge clamps at zero —
 *  a delta cannot drive any counter below zero, which protects
 *  against double-fires (e.g. two listeners both running the
 *  optimistic apply on the same mutation). */
export const EMAIL_STATS_OPTIMISTIC_DELTA_EVENT = 'email-stats-optimistic-delta'

/** Pure delta shape. Numeric fields are increments to add to the
 *  current stats; group-keyed objects (by_folder, by_category) are
 *  per-key increments. Omit any field that didn't change. */
export interface EmailStatsDelta {
  total?: number
  unread?: number
  starred?: number
  unprocessed?: number
  by_folder?: Record<string, number>
  by_category?: Record<string, number>
}

/** Dispatch an optimistic stats delta. Tiny module-level helper so
 *  callsites don't have to import the EVENT constant separately or
 *  remember the CustomEvent + detail wrapping shape. */
export function dispatchEmailStatsDelta(delta: EmailStatsDelta): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<EmailStatsDelta>(
      EMAIL_STATS_OPTIMISTIC_DELTA_EVENT,
      { detail: delta },
    ),
  )
}

/** Pure I→O: translate a bulk action + row count into the optimistic
 *  stats delta. Returns ``null`` for actions whose stats impact can't
 *  be derived from the action name alone (e.g. ``set_category`` — we
 *  don't know the prev categories of the affected rows so we let the
 *  watermark reconcile). ``count`` may be negative when called for
 *  rollback or corrective delta application; the merge handles
 *  negatives via clamp-at-zero. Exported for the pin test. */
export function bulkDeltaFor(
  action: string,
  count: number,
  _extras: Record<string, unknown> = {},
): EmailStatsDelta | null {
  if (count === 0) return null
  switch (action) {
    case 'mark_read':
      // Every affected row went unread → read. unread decreases.
      return { unread: -count }
    case 'mark_unread':
      return { unread: count }
    case 'star':
      return { starred: count }
    case 'unstar':
      return { starred: -count }
    case 'archive':
      // by_folder: inbox -count, archive +count. We can't know the
      // exact prev folder per row, so we assume the common case
      // (inbox→archive). If a row was already in archive the BE
      // ignores it (drops from affected count) and the reconciliation
      // step in ``bulkAction`` corrects the delta.
      return { by_folder: { inbox: -count, archive: count } }
    case 'unarchive':
      return { by_folder: { archive: -count, inbox: count } }
    case 'delete':
    case 'junk':
      // Soft bin: the row leaves the active mailbox total (it moves to
      // the Binned folder) but is NEVER hard-deleted on the backend.
      // 'junk' is delete's spam-intent sibling (also stamps category=
      // 'junk' + logs a reversible MANUAL event) but has the identical
      // mailbox-total impact, so it shares this delta.
      return { total: -count }
    case 'restore':
      // Symmetric undo of the soft bin — the row returns to the active
      // mailbox total.
      return { total: count }
    default:
      // set_category / reset_category / unknown — let the watermark
      // reconcile. The BE response cache TTL (60s) bounds the drift.
      return null
  }
}

/** Pure I→O merge — takes a stats snapshot + a delta, returns a new
 *  snapshot with the delta applied. Clamped at zero per field so
 *  the badges can never display a negative count even if a delta
 *  fires twice or out of order. Exported so the cadence pin test
 *  can assert the merge shape directly. */
export function mergeStatsDelta(
  prev: EmailStats,
  delta: EmailStatsDelta,
): EmailStats {
  const clampedAdd = (a: number | undefined, b: number | undefined): number => {
    const sum = (a ?? 0) + (b ?? 0)
    return sum < 0 ? 0 : sum
  }
  const mergeGroup = (
    prevGroup: Record<string, number> | undefined,
    deltaGroup: Record<string, number> | undefined,
  ): Record<string, number> => {
    if (!deltaGroup) return prevGroup ?? {}
    const out: Record<string, number> = { ...(prevGroup ?? {}) }
    for (const [key, inc] of Object.entries(deltaGroup)) {
      const next = (out[key] ?? 0) + inc
      out[key] = next < 0 ? 0 : next
    }
    return out
  }
  return {
    ...prev,
    total: clampedAdd(prev.total, delta.total),
    unread: clampedAdd(prev.unread, delta.unread),
    starred: clampedAdd(prev.starred, delta.starred),
    unprocessed: clampedAdd(prev.unprocessed, delta.unprocessed),
    by_folder: mergeGroup(prev.by_folder, delta.by_folder),
    by_category: mergeGroup(prev.by_category, delta.by_category),
  }
}
